import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, IconButton, Stack, Button, Tooltip } from '@mui/material';
import { Favorite, Delete, Download, ContentCopy } from '@mui/icons-material';

interface FavoriteItem {
  id: string;
  type: 'color' | 'palette' | 'gradient' | 'compare';
  name: string;
  data: any;
  date: string;
}

const FAVORITES_KEY = 'favorites_v1';

const FavoritesPanel: React.FC = () => {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [open, setOpen] = useState(false);

  const loadFavorites = () => {
    const favs = localStorage.getItem(FAVORITES_KEY);
    if (favs) setFavorites(JSON.parse(favs));
    else setFavorites([]);
  };

  useEffect(() => {
    loadFavorites();
    
    // Storage değiştiğinde güncelle
    const handleStorage = () => loadFavorites();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('favoriteAdded', handleStorage);
    
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('favoriteAdded', handleStorage);
    };
  }, []);

  const removeFavorite = (id: string) => {
    const updated = favorites.filter(f => f.id !== id);
    setFavorites(updated);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(updated));
  };

  const clearFavorites = () => {
    setFavorites([]);
    localStorage.removeItem(FAVORITES_KEY);
  };

  const copyColor = (hex: string) => {
    navigator.clipboard.writeText(hex);
  };

  const exportFavorites = () => {
    const data = JSON.stringify(favorites, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'favoriler.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 2000 }}>
      <Tooltip title="Favoriler">
        <IconButton 
          color="secondary" 
          sx={{ 
            bgcolor: 'white', 
            boxShadow: 3,
            '&:hover': { bgcolor: '#fff5f5' }
          }} 
          onClick={() => setOpen(!open)}
        >
          <Favorite color={open ? 'error' : 'disabled'} />
          {favorites.length > 0 && (
            <Box sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              bgcolor: 'error.main',
              color: 'white',
              borderRadius: '50%',
              width: 20,
              height: 20,
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {favorites.length}
            </Box>
          )}
        </IconButton>
      </Tooltip>
      {open && (
        <Paper sx={{ p: 2, minWidth: 350, maxHeight: 450, overflowY: 'auto', mt: 1, boxShadow: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Favorite color="error" sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Favorilerim ({favorites.length})</Typography>
            <Tooltip title="Dışa Aktar">
              <IconButton size="small" onClick={exportFavorites} disabled={favorites.length === 0}>
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button size="small" color="error" onClick={clearFavorites} disabled={favorites.length === 0}>
              Tümünü Sil
            </Button>
          </Box>
          <Stack spacing={1}>
            {favorites.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                Henüz favori eklenmedi.<br />
                Renk seçip ❤️ butonuna tıklayın!
              </Typography>
            )}
            {favorites.map(fav => (
              <Paper key={fav.id} sx={{ p: 1.5, display: 'flex', alignItems: 'center', bgcolor: '#fafafa', '&:hover': { bgcolor: '#f0f0f0' } }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={700}>{fav.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fav.type === 'color' ? 'Renk' : fav.type === 'palette' ? 'Palet' : fav.type === 'gradient' ? 'Gradient' : 'Karşılaştırma'} • {new Date(fav.date).toLocaleString('tr-TR')}
                  </Typography>
                  {fav.type === 'color' && (
                    <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                      <Box sx={{ width: 32, height: 32, bgcolor: fav.data.hex, borderRadius: 1, border: '1px solid #ccc' }} />
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{fav.data.hex}</Typography>
                      <Tooltip title="Kopyala">
                        <IconButton size="small" onClick={() => copyColor(fav.data.hex)}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                  {fav.type === 'palette' && (
                    <Stack direction="row" spacing={0.5} mt={1}>
                      {fav.data.colors?.map((c: any, i: number) => (
                        <Tooltip key={i} title={c.hex}>
                          <Box sx={{ width: 24, height: 24, bgcolor: c.hex, borderRadius: 0.5, border: '1px solid #eee', cursor: 'pointer' }} onClick={() => copyColor(c.hex)} />
                        </Tooltip>
                      ))}
                    </Stack>
                  )}
                  {fav.type === 'gradient' && (
                    <Box sx={{ width: 100, height: 24, borderRadius: 1, mt: 1, background: fav.data.css, border: '1px solid #eee' }} />
                  )}
                  {fav.type === 'compare' && (
                    <Stack direction="row" spacing={0.5} mt={1}>
                      <Box sx={{ width: 24, height: 24, bgcolor: fav.data.color1, borderRadius: 0.5, border: '1px solid #eee' }} />
                      <Box sx={{ width: 24, height: 24, bgcolor: fav.data.color2, borderRadius: 0.5, border: '1px solid #eee' }} />
                    </Stack>
                  )}
                </Box>
                <IconButton size="small" color="error" onClick={() => removeFavorite(fav.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default FavoritesPanel;